import { Activity, BadgeCheck, Gauge, Layers, Ruler, ShieldAlert, TrendingUp } from 'lucide-react';
import { t, useLanguage } from '@/app/utils/i18n';
import type { AqlGrowthDashboardPoint, GrowthWarningLevel } from '@/app/services/growth-service';

interface GrowthKpiCardsProps {
  series: AqlGrowthDashboardPoint[];
  loading?: boolean;
}

interface KpiSummary {
  medianSize: number | null;
  avgSize: number | null;
  growthMmPerDay: number | null;
  sampleCount: number | null;
  uniformity: number | null;
  confidence: number | null;
  warningLevel: GrowthWarningLevel;
}

const formatMm = (value: number | null) =>
  value === null || value === undefined ? '—' : `${value.toFixed(1)} mm`;

const formatPct = (value: number | null) =>
  value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;

const formatRate = (value: number | null, unit: string) => {
  if (value === null || value === undefined) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)} ${unit}`;
};

const computeSummary = (series: AqlGrowthDashboardPoint[]): KpiSummary => {
  if (!series.length) {
    return {
      medianSize: null,
      avgSize: null,
      growthMmPerDay: null,
      sampleCount: null,
      uniformity: null,
      confidence: null,
      warningLevel: 'normal'
    };
  }

  const latest = series[series.length - 1];
  const earliest = series[0];

  let growthMmPerDay: number | null = null;
  if (
    series.length >= 2 &&
    latest.median_size !== null &&
    earliest.median_size !== null &&
    latest.period_start !== earliest.period_start
  ) {
    const days = Math.max(
      1,
      Math.round(
        (new Date(latest.period_start).getTime() - new Date(earliest.period_start).getTime()) / (1000 * 60 * 60 * 24)
      )
    );
    growthMmPerDay = (latest.median_size - earliest.median_size) / days;
  }

  return {
    medianSize: latest.median_size,
    avgSize: latest.avg_size,
    growthMmPerDay,
    sampleCount: latest.sample_count,
    uniformity: latest.uniformity_score,
    confidence: latest.confidence,
    warningLevel: latest.warning_level ?? 'normal'
  };
};

interface KpiCardProps {
  label: string;
  value: string;
  icon: typeof Ruler;
  hint?: string;
  tone?: 'default' | 'warning' | 'critical' | 'positive';
}

const toneClass = (tone: KpiCardProps['tone']) => {
  switch (tone) {
    case 'warning':
      return 'border-amber-400/45 bg-amber-500/8 text-amber-700 dark:text-amber-100';
    case 'critical':
      return 'border-red-500/45 bg-red-500/8 text-red-700 dark:text-red-100';
    case 'positive':
      return 'border-emerald-500/35 bg-emerald-500/8 text-emerald-700 dark:text-emerald-100';
    default:
      return 'border-border bg-card text-foreground';
  }
};

function KpiCard({ label, value, icon: Icon, hint, tone = 'default' }: KpiCardProps) {
  return (
    <div className={`flex flex-col gap-2 rounded-2xl border p-4 shadow-sm ${toneClass(tone)}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <span className="text-2xl font-bold leading-none tracking-tight">{value}</span>
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="h-3 w-24 animate-pulse rounded bg-muted/70" />
      <div className="h-7 w-32 animate-pulse rounded bg-muted/70" />
      <div className="h-3 w-40 animate-pulse rounded bg-muted/40" />
    </div>
  );
}

export function GrowthKpiCards({ series, loading }: GrowthKpiCardsProps) {
  const language = useLanguage();
  const tr = (key: string) => t('vision', key, language);

  if (loading) {
    return (
      <section className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7">
        {Array.from({ length: 7 }).map((_, idx) => (
          <SkeletonCard key={idx} />
        ))}
      </section>
    );
  }

  const summary = computeSummary(series);
  const growthTone: KpiCardProps['tone'] = summary.growthMmPerDay !== null && summary.growthMmPerDay > 0
    ? 'positive'
    : 'default';
  const warningTone: KpiCardProps['tone'] =
    summary.warningLevel === 'critical' ? 'critical' : summary.warningLevel === 'warning' ? 'warning' : 'positive';

  return (
    <section className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7">
      <KpiCard
        label={tr('currentMedianSize')}
        value={formatMm(summary.medianSize)}
        icon={Ruler}
        hint={tr('cohortMedianLatestPeriod')}
      />
      <KpiCard
        label={tr('averageSize')}
        value={formatMm(summary.avgSize)}
        icon={Layers}
        hint={tr('secondaryIndicator')}
      />
      <KpiCard
        label={tr('growthRate')}
        value={formatRate(summary.growthMmPerDay, tr('mmPerDay'))}
        icon={TrendingUp}
        hint={tr('medianDeltaSelectedRange')}
        tone={growthTone}
      />
      <KpiCard
        label={tr('validSample')}
        value={summary.sampleCount === null ? '—' : summary.sampleCount.toString()}
        icon={Activity}
        hint={tr('lastPeriodAcceptedMeasurements')}
      />
      <KpiCard
        label={tr('cohortUniformity')}
        value={formatPct(summary.uniformity)}
        icon={Gauge}
        hint={tr('lowerMoreDispersion')}
      />
      <KpiCard
        label={tr('measurementConfidence')}
        value={formatPct(summary.confidence)}
        icon={BadgeCheck}
        hint={tr('averageAiConfidence')}
      />
      <KpiCard
        label={tr('warningLevel')}
        value={
          summary.warningLevel === 'critical'
            ? tr('warningCritical')
            : summary.warningLevel === 'warning'
              ? tr('warningWarning')
              : tr('warningNormal')
        }
        icon={ShieldAlert}
        tone={warningTone}
      />
    </section>
  );
}
